import React, { useState } from "react";
import { motion } from "framer-motion";

const TarotCard = ({ card, position, index }) => {
  const [imageError, setImageError] = useState(false);
  const [hasTriedPlaceholder, setHasTriedPlaceholder] = useState(false);

  // Handle image loading errors with multiple fallback options
  const handleImageError = (e) => {
    // If we've already tried the placeholder, just return to avoid infinite loop
    if (hasTriedPlaceholder) {
      return;
    }

    // If we've already tried the fallbacks, use the placeholder
    if (imageError) {
      e.target.src = "/images/place-holder.jpg";
      setHasTriedPlaceholder(true);
      return;
    }

    // Try alternative naming patterns
    const cardName = card.name.toLowerCase().replace(/\s+/g, "_");

    // Try various naming patterns
    if (card.name.toLowerCase().startsWith("the ")) {
      // For cards like "The Fool" -> try "the_fool.jpg" or "the_fool.svg"
      e.target.src = `/images/${cardName}.jpg`;
      e.target.onerror = () => {
        e.target.src = `/images/${cardName}.svg`;
        e.target.onerror = () => {
          if (!hasTriedPlaceholder) {
            e.target.src = "/images/place-holder.jpg";
            setHasTriedPlaceholder(true);
          }
        };
      };
    } else if (card.name.includes(" of ")) {
      // For cards like "Five of Wands" -> try "five_of_wands.jpg"
      const simpleName = card.name
        .toLowerCase()
        .replace(/\s+of\s+/g, "_of_")
        .replace(/\s+/g, "_");
      e.target.src = `/images/${simpleName}.jpg`;
      e.target.onerror = () => {
        if (!hasTriedPlaceholder) {
          e.target.src = "/images/place-holder.jpg";
          setHasTriedPlaceholder(true);
        }
      };
    } else {
      // Other cards - try direct lowercase with underscores
      e.target.src = `/images/${cardName}.jpg`;
      e.target.onerror = () => {
        if (!hasTriedPlaceholder) {
          e.target.src = "/images/place-holder.jpg";
          setHasTriedPlaceholder(true);
        }
      };
    }

    // Mark that we've tried fallbacks
    setImageError(true);
  };

  // *** Add this line for debugging ***
  console.log(
    `Rendering Card ID: ${card.id}, Name: ${card.name}, Reversed: ${card.isReversed}, Received Reversed Meaning:`,
    card.reversedMeaning
  );

  return (
    <motion.div
      key={card.id}
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: index * 0.15,
        type: "spring",
        stiffness: 100,
        damping: 10,
      }}
    >
      <div className="flex flex-col items-start text-left">
        {/* Card Image */}
        <div className="w-full mb-3">
          <img
            src={card.image || "/images/place-holder.jpg"}
            alt={card.name}
            className={`w-full h-auto mx-auto border border-gray-200 ${
              card.isReversed ? "rotate-180" : ""
            }`}
            onError={handleImageError}
          />
        </div>

        {/* Position Label */}
        <p className="text-base font-semibold text-black/90 mb-2 lowercase tracking-widest">
          {position || ["Past", "Present", "Future"][index]}
        </p>

        {/* Meaning */}
        <p className="font-light text-xs text-black mb-1 tracking-wider">
          {(() => {
            // Select meaning based on orientation
            const currentMeaning = card.isReversed
              ? card.reversedMeaning
              : card.meaning;

            // Default display values
            let prefixText = card.name;
            let descriptionText = "Meaning unavailable";
            let isActuallyReversed = card.isReversed; // Track if we are in reversed mode

            if (currentMeaning) {
              const parts = currentMeaning.split(": ");
              if (parts.length === 2) {
                // If split works, use the parts
                prefixText = parts[0].trim(); // e.g., "Reversed" or "Three of Cups"
                descriptionText = parts[1].trim(); // e.g., "Obstacles..." or "Expansion..."
              } else {
                // If split fails, use the whole string as description, default prefix
                descriptionText = currentMeaning.trim();
              }
            }

            // Capitalize first letter of description
            const formattedDescription =
              descriptionText.length > 0
                ? descriptionText.charAt(0).toUpperCase() +
                  descriptionText.slice(1) // Keep rest as is, don't force lowercase
                : "";

            // Render based on the determined state
            return isActuallyReversed ? (
              <>
                <span className="font-semibold">
                  {card.name} {/* Always show card name first when reversed */}
                </span>
                <span className="text-red-600 font-semibold">
                  {" "}
                  - REVERSED: {/* Use fixed "REVERSED:" prefix */}
                </span>{" "}
                {formattedDescription} {/* Show the meaning part */}
              </>
            ) : (
              <>
                <span className="font-semibold">{prefixText}:</span>{" "}
                {formattedDescription}
              </>
            );
          })()}
        </p>
      </div>
    </motion.div>
  );
};

export default TarotCard;
